// src/pages/SoporteTecnico.tsx

import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../security/AuthLogin";
import {
  fetchSoporteMensajes,
  sendSoporteMensaje,
  fetchArchivosSoporte,
} from "../api/soportesApi";
import { getAuthToken } from "../security/AuthLogin";
import { io, Socket } from "socket.io-client";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import "../css/favorito.css";
import { fetchSoporteTecnicoDetalle } from "../api/soportesTecnicosApi";
import ChatComponent from "../components/ChatComponent";

// ---------------------
// INTERFACES
// ---------------------
interface SoporteData {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  enlace: string | null;
  estado_concurso: string;
  estado_soporte_favorito: string | null;
  id_soporte: number;
  cliente_nombre?: string;
  cliente_email?: string;
  cliente_profilepic?: string;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
}

interface MensajeSoporte {
  id_soporte_detalle: number;
  id_soporte: number;
  id_usuario: string;
  f_respuesta: string;
  hora_respuesta: string;
  texto: string;
  es_cliente: boolean;
}

// <-- CAMBIO: usar dateFolder en lugar de timestamp
interface ArchivoSoporte {
  path: string;
  uploaderRole: "cliente" | "admin" | "tecnico";
  dateFolder?: string | null;
}

interface TecnicoData {
  id_usuario: string;
  nombre: string;
  email: string;
}

// ---------------------
// CONSTANTES / HELPERS
// ---------------------
const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;

const mapEstado = (estado: string | null): string => {
  if (!estado) return "En estudio";
  switch (estado.toLowerCase()) {
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

// ---------------------
// COMPONENTE PRINCIPAL
// ---------------------
const SoporteTecnico: React.FC = () => {
  const { id_concurso, id_usuario } = useParams<{
    id_concurso: string;
    id_usuario: string;
  }>();
  const { userRole } = useAuth();

  const [loading, setLoading] = useState(false);
  const [soporte_tecnico, setSoporteTecnico] = useState<SoporteData | null>(
    null
  );
  const [, setMensajes] = useState<MensajeSoporte[]>([]);
  const [, setArchivos] = useState<ArchivoSoporte[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [, setAlguienTyping] = useState({
    isTyping: false,
    quien: "",
  });
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [tecnicosActivos, setTecnicosActivos] = useState<TecnicoData[]>([]);
  const [selectedTecnico, setSelectedTecnico] = useState("");
  const [showReassignModal, setShowReassignModal] = useState(false);

  // ★ NUEVO: estados para modal de confirmación
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmType, setConfirmType] = useState<
    "baja" | "administrativo" | "juridico" | null
  >(null);
  const [confirmMessage, setConfirmMessage] = useState("");

  // ★ NUEVO: configuración tipos de soporte
  const tipoConfig = {
    baja: {
      label: "Baja estadística",
      endpoint: "/api/baja-estadistica",
      redirectPath: (c: string, u: string) => `/baja-estadistica/${c}/${u}`,
    },
    administrativo: {
      label: "Soporte administrativo",
      endpoint: "/api/soporte-administrativo",
      redirectPath: (c: string , u: string) => `/soporte-administrativo/${c}/${u}`,
    },
    juridico: {
      label: "Soporte jurídico",
      endpoint: "/api/soporte-juridico",
      redirectPath: (c: string, u: string) => `/soporte-juridico/${c}/${u}`,
    },
  };

  // ★ NUEVO: comprueba existencia de soporte
  const checkSupportExists = async (type: keyof typeof tipoConfig) => {
    if (!soporte_tecnico) return false;
    const token = getAuthToken();
    const resp = await fetch(
      `${BASE_URL}/api/soporte-existe?tipo=${type}&id_concurso=${soporte_tecnico.id_concurso}&id_usuario=${soporte_tecnico.id_usuario}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!resp.ok) return false;
    const json = await resp.json();
    return json.exists as boolean;
  };

  // ★ NUEVO: wrapper click soportes
  const handleButtonClick = async (type: keyof typeof tipoConfig) => {
    if (!soporte_tecnico) return;
    const exists = await checkSupportExists(type);
    if (exists) {
      await invokeSupportHandler(type, "");
    } else {
      setConfirmType(type);
      setConfirmMessage("");
      setConfirmModalVisible(true);
    }
  };

  // ★ NUEVO: invoca endpoint, envía mensaje, redirige
  const invokeSupportHandler = async (
    type: keyof typeof tipoConfig,
    initialMessage: string
  ) => {
    if (!soporte_tecnico) return;
    const { endpoint, redirectPath } = tipoConfig[type];
    try {
      const token = getAuthToken();
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id_concurso: soporte_tecnico.id_concurso,
          id_usuario: soporte_tecnico.id_usuario,
        }),
      });
      if (!resp.ok) throw new Error((await resp.text()) || "");
      const data = await resp.json();
      if (data.success && data.id_soporte) {
        if (initialMessage.trim()) {
          await sendSoporteMensaje(
            data.id_soporte,
            DOMPurify.sanitize(initialMessage)
          );
        }
        window.location.href = redirectPath(
          soporte_tecnico.id_concurso,
          soporte_tecnico.id_usuario
        );
      }
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  // ★ NUEVO: confirmar modal
  const handleConfirmYes = async () => {
    if (confirmType) {
      await invokeSupportHandler(confirmType, confirmMessage);
      setConfirmModalVisible(false);
      setConfirmType(null);
      setConfirmMessage("");
    }
  };
  const handleConfirmNo = () => {
    setConfirmModalVisible(false);
    setConfirmType(null);
    setConfirmMessage("");
  };

  // ---------------------
  // CONEXIÓN AL SOCKET
  // ---------------------
  useEffect(() => {
    const s = io(BASE_URL, {
      withCredentials: true,
      auth: (cb) => cb({ token: localStorage.getItem("authToken") }),
    });
    setSocket(s);
    return () => {
      s.disconnect();
    };
  }, []);

  // ---------------------
  // CARGA DEL SOPORTE
  // ---------------------
  useEffect(() => {
    if (id_concurso && id_usuario) {
      loadSoporte(id_concurso, id_usuario);
    }
  }, [id_concurso, id_usuario]);

  const loadSoporte = async (concursoId: string, userIdParam: string) => {
    setLoading(true);
    try {
      const data = await fetchSoporteTecnicoDetalle(concursoId, userIdParam);
      if (data.success) {
        setSoporteTecnico(data.soporte_tecnico as SoporteData);
        if (data.soporte_tecnico?.id_soporte) {
          await loadChat(data.soporte_tecnico.id_soporte);
          await loadArchivos(data.soporte_tecnico.id_soporte);
        }
      }
    } catch (error: any) {
      console.error("Error loading soporte técnico:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadChat = async (idSoporte: number) => {
    try {
      const resp = await fetchSoporteMensajes(idSoporte);
      if (resp.success) {
        setMensajes(resp.messages);
        scrollToBottom();
      }
    } catch (error: any) {
      console.error("Error loading chat:", error);
    }
  };

  const loadArchivos = async (idSoporte: number) => {
    try {
      const resp = await fetchArchivosSoporte(idSoporte);
      if (resp.success) {
        setArchivos(resp.archivos);
      }
    } catch (error: any) {
      console.error("Error loading files:", error);
    }
  };

  // ---------------------
  // SOCKET: ESCUCHAR EVENTOS
  // ---------------------
  useEffect(() => {
    if (socket && soporte_tecnico?.id_soporte) {
      socket.emit("joinSoporte", soporte_tecnico.id_soporte);
      socket.on("nuevoMensaje", (mensaje: MensajeSoporte) => {
        setMensajes((prev) => [...prev, mensaje]);
        scrollToBottom();
      });
      socket.on("alguienEstaEscribiendo", ({ isTyping, quien }) => {
        setAlguienTyping({ isTyping, quien });
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            setAlguienTyping({ isTyping: false, quien: "" });
          }, 5000);
        }
      });
    }
    return () => {
      if (socket) {
        socket.off("nuevoMensaje");
        socket.off("alguienEstaEscribiendo");
      }
    };
  }, [socket, soporte_tecnico?.id_soporte]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ---------------------
  // CARGA DE TÉCNICOS
  // ---------------------
  useEffect(() => {
    if (userRole === "admin") {
      fetchTecnicos();
    }
  }, [userRole]);

  const fetchTecnicos = async () => {
    try {
      const token = getAuthToken();
      const resp = await fetch(`${BASE_URL}/api/usuarios/tecnicos-activos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await resp.json();
      if (json.success) {
        setTecnicosActivos(json.tecnicos);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  };

  // ---------------------
  // LÓGICA DE DUEÑOS
  // ---------------------
  const handleDueñoAction = async (
    action: "becomeOwner" | "dropOwner" | "assignTo",
    newDueno?: string
  ) => {
    if (!soporte_tecnico?.id_soporte) return;
    try {
      const resp = await fetch(
        `${BASE_URL}/api/soportes/${soporte_tecnico.id_soporte}/dueno`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ action, ...(newDueno && { newDueno }) }),
        }
      );
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Error al actualizar dueño: ${txt}`);
      }
      const data = await resp.json();
      if (data.success) {
        // Recargar el soporte técnico para actualizar la info
        await loadSoporte(
          soporte_tecnico.id_concurso,
          soporte_tecnico.id_usuario
        );
        alert(data.message);
      } else {
        alert(data.message || "Error al cambiar dueño");
      }
    } catch (error: any) {
      console.error("Error updating owner:", error);
      alert(`Error al cambiar dueño: ${error.message}`);
    }
  };

  const handleReassignModalOpen = () => {
    setShowReassignModal(true);
  };

  const handleReassignConfirm = async () => {
    if (!selectedTecnico) {
      alert("Selecciona un técnico");
      return;
    }
    await handleDueñoAction("assignTo", selectedTecnico);
    setShowReassignModal(false);
  };

  // ---------------------
  // RENDER
  // ---------------------
  if (!soporte_tecnico) {
    return (
      <div className="concurso-container">
        {loading ? <p>Cargando...</p> : <p>No se encontró soporte técnico</p>}
      </div>
    );
  }

  const soyDueño = false; // Ajustar si se compara con el ID real
  const esAdmin = userRole === "admin";
  const esTec = userRole === "tecnico";
  const esAdminOTec = esAdmin || esTec;
  const hayDueño = !!soporte_tecnico.dueno_nombre;
  const estadoVisible = mapEstado(
    soporte_tecnico.estado_soporte_favorito || "En estudio"
  );

  return (
    <div className="favorito-page">
      {/* Botones flotantes */}
      <aside className="sticky-sidebar">
        {/* enlaces estáticos */}
        <button className="sidebar-button">
          <Link
            to={`/concurso/${soporte_tecnico.id_concurso}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver concurso
          </Link>
        </button>
        <button className="sidebar-button">
          <Link
            to={`/favorito/${soporte_tecnico.id_concurso}/${soporte_tecnico.id_usuario}`}
            style={{ color: "inherit", textDecoration: "none" }}
          >
            Ver favorito
          </Link>
        </button>
        {/* botones con confirmación */}
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("baja")}
        >
          Baja estadística
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("administrativo")}
        >
          Soporte administrativo
        </button>
        <button
          className="sidebar-button"
          onClick={() => handleButtonClick("juridico")}
        >
          Soporte jurídico
        </button>
      </aside>

      <div className="concurso-container">
        <div
          className="concurso-header"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div>
            <h1>Soporte Técnico</h1>
            <p>Expediente: {soporte_tecnico.codigo_expediente}</p>
            {esAdminOTec &&
              (hayDueño ? (
                <p>
                  Responsable: {soporte_tecnico.dueno_nombre} (
                  {soporte_tecnico.dueno_email})
                </p>
              ) : (
                <p>Sin responsable asignado</p>
              ))}
          </div>
          {esAdminOTec && (
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              {!hayDueño && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("becomeOwner")}
                >
                  Asignarme
                </button>
              )}
              {hayDueño && (soyDueño || esAdmin) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleDueñoAction("dropOwner")}
                >
                  Liberar
                </button>
              )}
              {esAdmin && (
                <button
                  className="btn btn-secondary"
                  onClick={handleReassignModalOpen}
                >
                  Reasignar
                </button>
              )}
            </div>
          )}
        </div>

        {showReassignModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Reasignar soporte</h3>
              <select
                value={selectedTecnico}
                onChange={(e) => setSelectedTecnico(e.target.value)}
              >
                <option value="">Seleccionar técnico...</option>
                {tecnicosActivos.map((t) => (
                  <option key={t.id_usuario} value={t.id_usuario}>
                    {t.nombre} ({t.email})
                  </option>
                ))}
              </select>
              <div style={{ marginTop: "1rem" }}>
                <button
                  onClick={handleReassignConfirm}
                  style={{ marginRight: "1rem" }}
                >
                  Confirmar
                </button>
                <button onClick={() => setShowReassignModal(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <h2>Detalles</h2>
          <div className="info-grid">
            <div className="field">
              <label>Título</label>
              <span>{soporte_tecnico.titulo}</span>
            </div>
            <div className="enlaces-container">
              <label>Enlace</label>
              {soporte_tecnico.enlace ? (
                <a
                  href={soporte_tecnico.enlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="enlace-oficial"
                >
                  Enlace oficial
                </a>
              ) : (
                <span>No disponible</span>
              )}
            </div>
          </div>
          <div style={{ marginTop: "1rem" }}>
            <strong>Estado Soporte:</strong> <span>{estadoVisible}</span>
          </div>
        </div>

        <div className="section">
          <h2>Comunicación</h2>
          <ChatComponent
            idSoporte={soporte_tecnico.id_soporte}
            clienteNombre={soporte_tecnico.cliente_nombre}
            clienteEmail={soporte_tecnico.cliente_email}
            clienteProfilePic={soporte_tecnico.cliente_profilepic}
            sendText={true}
            allowFileUpload={true}
            showTypingIndicator={true}
          />
        </div>
      </div>
      {/* ★ NUEVO: modal de confirmación */}
      {confirmModalVisible && confirmType && (
        <div className="modal-overlay-soportes">
          <div className="modal-content-soportes">
            <h3>
              ¿Quieres solicitar el{" "}
              <strong>{tipoConfig[confirmType].label}</strong>?
            </h3>
            <textarea
              value={confirmMessage}
              onChange={(e) => setConfirmMessage(e.target.value)}
              placeholder="Describe tu solicitud…"
              rows={4}
              style={{ width: "100%", margin: "1rem 0" }}
            />
            <div className="modal-buttons-soportes">
              <button
                onClick={handleConfirmYes}
                disabled={!confirmMessage.trim()}
              >
                Sí
              </button>
              <button onClick={handleConfirmNo}>No</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoporteTecnico;
